import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Filter, X } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';

export interface FilterState {
  status?: string;
  mode?: string;
  carrierId?: string;
  dateRange?: DateRange;
  exceptionsOnly: boolean;
}

interface AdvancedFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  carriers?: Array<{ id: string; name: string }>;
}

// Shipment status options
const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'BOOKED', label: 'Booked' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'DELAYED', label: 'Delayed' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// Transport mode options
const MODE_OPTIONS = [
  { value: 'ALL', label: 'All Modes' },
  { value: 'OCEAN', label: 'Ocean' },
  { value: 'AIR', label: 'Air' },
  { value: 'ROAD_FTL', label: 'Road FTL' },
  { value: 'ROAD_LTL', label: 'Road LTL' },
  { value: 'RAIL', label: 'Rail' },
];

export function AdvancedFilters({ filters, onFiltersChange, carriers = [] }: AdvancedFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Count active filters
  const activeFilterCount = [
    filters.status && filters.status !== 'ALL',
    filters.mode && filters.mode !== 'ALL',
    filters.carrierId,
    filters.dateRange?.from || filters.dateRange?.to,
    filters.exceptionsOnly,
  ].filter(Boolean).length;

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const removeFilter = (key: keyof FilterState) => {
    const newFilters = { ...filters };
    if (key === 'dateRange') {
      newFilters.dateRange = undefined;
    } else if (key === 'exceptionsOnly') {
      newFilters.exceptionsOnly = false;
    } else {
      delete newFilters[key];
    }
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    onFiltersChange({
      exceptionsOnly: false,
    });
  };

  // Get carrier name by ID
  const getCarrierName = (carrierId: string) => {
    const carrier = carriers.find((c) => c.id === carrierId);
    return carrier?.name || carrierId;
  };

  return (
    <div className="space-y-2">
      {/* Filter Button with Badge */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="relative">
            <Filter className="mr-2 h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="default" className="ml-2 h-5 min-w-5 rounded-full px-1.5 text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-80" align="start">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Advanced Filters</h4>
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear All
                </Button>
              )}
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={filters.status || 'ALL'}
                onValueChange={(value) =>
                  updateFilter('status', value === 'ALL' ? undefined : value)
                }
              >
                <SelectTrigger id="status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Transport Mode Filter */}
            <div className="space-y-2">
              <Label htmlFor="mode">Transport Mode</Label>
              <Select
                value={filters.mode || 'ALL'}
                onValueChange={(value) => updateFilter('mode', value === 'ALL' ? undefined : value)}
              >
                <SelectTrigger id="mode">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  {MODE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Carrier Filter */}
            {carriers.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="carrier">Carrier</Label>
                <Select
                  value={filters.carrierId || 'ALL'}
                  onValueChange={(value) =>
                    updateFilter('carrierId', value === 'ALL' ? undefined : value)
                  }
                >
                  <SelectTrigger id="carrier">
                    <SelectValue placeholder="Select carrier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Carriers</SelectItem>
                    {carriers.map((carrier) => (
                      <SelectItem key={carrier.id} value={carrier.id}>
                        {carrier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date Range Filter */}
            <div className="space-y-2">
              <Label>Date Range</Label>
              <Calendar
                mode="range"
                selected={filters.dateRange}
                onSelect={(range) => updateFilter('dateRange', range)}
                numberOfMonths={1}
                className="rounded-md border"
              />
              {filters.dateRange?.from && filters.dateRange?.to && (
                <p className="text-xs text-muted-foreground">
                  {format(filters.dateRange.from, 'MMM d, yyyy')} -{' '}
                  {format(filters.dateRange.to, 'MMM d, yyyy')}
                </p>
              )}
            </div>

            {/* Exceptions Only Toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="exceptions" className="cursor-pointer">
                Show Exceptions Only
              </Label>
              <Switch
                id="exceptions"
                checked={filters.exceptionsOnly}
                onCheckedChange={(checked) => updateFilter('exceptionsOnly', checked)}
              />
            </div>

            {/* Apply Button */}
            <Button className="w-full" onClick={() => setIsOpen(false)} size="sm">
              Apply Filters
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Active Filter Badges */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.status && filters.status !== 'ALL' && (
            <Badge variant="secondary" className="gap-1">
              Status: {STATUS_OPTIONS.find((o) => o.value === filters.status)?.label}
              <X
                className="h-3 w-3 cursor-pointer hover:text-foreground"
                onClick={() => removeFilter('status')}
              />
            </Badge>
          )}

          {filters.mode && filters.mode !== 'ALL' && (
            <Badge variant="secondary" className="gap-1">
              Mode: {MODE_OPTIONS.find((o) => o.value === filters.mode)?.label}
              <X
                className="h-3 w-3 cursor-pointer hover:text-foreground"
                onClick={() => removeFilter('mode')}
              />
            </Badge>
          )}

          {filters.carrierId && (
            <Badge variant="secondary" className="gap-1">
              Carrier: {getCarrierName(filters.carrierId)}
              <X
                className="h-3 w-3 cursor-pointer hover:text-foreground"
                onClick={() => removeFilter('carrierId')}
              />
            </Badge>
          )}

          {(filters.dateRange?.from || filters.dateRange?.to) && (
            <Badge variant="secondary" className="gap-1">
              Date: {filters.dateRange?.from ? format(filters.dateRange.from, 'MMM d') : '?'} -{' '}
              {filters.dateRange?.to ? format(filters.dateRange.to, 'MMM d') : '?'}
              <X
                className="h-3 w-3 cursor-pointer hover:text-foreground"
                onClick={() => removeFilter('dateRange')}
              />
            </Badge>
          )}

          {filters.exceptionsOnly && (
            <Badge variant="secondary" className="gap-1">
              Exceptions Only
              <X
                className="h-3 w-3 cursor-pointer hover:text-foreground"
                onClick={() => removeFilter('exceptionsOnly')}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
